import type { Product } from "@shared/types/product";
import type { ColumnFiltersState, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import {
  type AdminCatalogueRow,
  type CatalogueField,
  matchesBadgeSelection,
  matchesCatalogueQuery,
  matchesMissingSelection,
  matchesStatusSelection,
  toCatalogueRows,
  toFilterSelection,
} from "../admin/admin-catalogue-rows";
import { ADMIN_PRODUCTS_COLUMN_IDS } from "../admin/admin-products-columns";
import { useAdminProductsUrlState } from "./admin-products-url-state";

const resetPage = (prev: PaginationState): PaginationState => ({ ...prev, pageIndex: 0 });

const clampPageIndex = (pagination: PaginationState, matchCount: number): number => {
  const lastPage = Math.max(0, Math.ceil(matchCount / pagination.pageSize) - 1);
  return Math.min(pagination.pageIndex, lastPage);
};

const filterValue = (filters: ColumnFiltersState, columnId: string): string[] =>
  toFilterSelection(filters.find((filter) => filter.id === columnId)?.value);

// Mirrors the table's own filtering so the page index can be clamped without
// reaching into the table instance.
const countMatches = (rows: AdminCatalogueRow[], search: string, filters: ColumnFiltersState): number => {
  const statuses = filterValue(filters, ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS);
  const badges = filterValue(filters, ADMIN_PRODUCTS_COLUMN_IDS.BADGES);
  return rows.filter(
    (row) =>
      matchesCatalogueQuery(row.__searchIndex, search) &&
      matchesStatusSelection(row.webStatus, statuses) &&
      matchesBadgeSelection(row.badges, badges)
  ).length;
};

/**
 * Search, filter, sort and pagination state for the admin catalogue, kept in
 * the URL so a refresh or a shared link restores it. Every filter change
 * returns to the first page.
 */
export const useAdminProductsViewState = (products: Product[], isError: boolean) => {
  const [urlState, updateUrlState] = useAdminProductsUrlState();
  const { search, columnFilters, missing, sorting, pagination } = urlState;

  const rows = useMemo(() => toCatalogueRows(products), [products]);
  // "Missing" is applied before the table, since the metrics share its state.
  const tableRows = useMemo(() => (isError ? [] : rows.filter((row) => matchesMissingSelection(row, missing))), [isError, rows, missing]);
  const matchCount = useMemo(() => countMatches(tableRows, search, columnFilters), [tableRows, search, columnFilters]);
  const controlledPagination = useMemo(
    () => ({ pageSize: pagination.pageSize, pageIndex: clampPageIndex(pagination, matchCount) }),
    [pagination, matchCount]
  );

  const setSearch = useCallback(
    (value: string) => updateUrlState((current) => ({ search: value, pagination: resetPage(current.pagination) })),
    [updateUrlState]
  );

  const setMissing = useCallback(
    (next: CatalogueField[]) => updateUrlState((current) => ({ missing: next, pagination: resetPage(current.pagination) })),
    [updateUrlState]
  );

  const toggleMissing = useCallback(
    (field: CatalogueField) => setMissing(missing.includes(field) ? missing.filter((item) => item !== field) : [...missing, field]),
    [missing, setMissing]
  );

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) =>
      updateUrlState((current) => ({
        columnFilters: typeof updater === "function" ? updater(current.columnFilters) : updater,
        pagination: resetPage(current.pagination),
      })),
    [updateUrlState]
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => updateUrlState((current) => ({ sorting: typeof updater === "function" ? updater(current.sorting) : updater })),
    [updateUrlState]
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      const current = controlledPagination;
      const next = typeof updater === "function" ? updater(current) : updater;
      if (next.pageIndex === current.pageIndex && next.pageSize === current.pageSize) return;
      updateUrlState({ pagination: next.pageSize === current.pageSize ? next : resetPage(next) });
    },
    [updateUrlState, controlledPagination]
  );

  return {
    rows,
    tableRows,
    search,
    setSearch,
    columnFilters,
    onColumnFiltersChange,
    missing,
    setMissing,
    toggleMissing,
    sorting,
    onSortingChange,
    pagination: controlledPagination,
    onPaginationChange,
  };
};
